import { isEmpty } from 'lodash/fp';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NoItemResult from '../../../components/results/NoItemResult';
import GroupedColumnChart from './Charts/GroupedColumnChart';
import { useDashboardContext } from './DashboardContext';
import useLeadsSubmittedData from './data/useLeadsSubmittedData';
import { FullScreenWrapper } from './shared/FullScreenWrapper';
import { type ChartDataProps, type Datum } from './shared/typings';
import useLegendSelectedConfig from './shared/useLegendSelectedConfig';
import useSeriesColors from './shared/useSeriesColors';
import useSeriesFields from './shared/useSeriesField';

export const LeadChartViewID = 'leadChart';

const LeadChartContent = ({ data, t, noData }: ChartDataProps) => {
    const { currentMonth, lastMonth } = useSeriesFields();
    const legendSelected = useLegendSelectedConfig({ currentMonth, lastMonth });
    const color = useSeriesColors();

    if (noData) {
        return <NoItemResult subTitle={t('dashboard:noData.message')} />;
    }

    return (
        <GroupedColumnChart
            {...t('dashboard:leadChart.chart', { returnObjects: true })}
            color={color}
            data={data}
            visible={legendSelected}
            xField="day"
            yField="noOfSubmitted"
        />
    );
};

const LeadChart = () => {
    const { t } = useTranslation(['dashboard']);
    const [data, setData] = useState<Datum | null>(null);
    const { isLoading, error, generateData } = useLeadsSubmittedData();

    useEffect(() => {
        generateData(result => setData(result));
    }, [generateData, setData]);

    const noData = useMemo(() => (data ? Object.values(data).every(isEmpty) : true), [data]);

    const { setVisible } = useDashboardContext();

    useEffect(() => {
        setVisible({ [LeadChartViewID]: !error });
    }, [error, setVisible]);

    if (error) {
        return null;
    }

    return (
        <FullScreenWrapper disabledFullScreen={noData} isLoading={isLoading} title={t('dashboard:leadChart.title')}>
            <LeadChartContent data={data} noData={noData} t={t} />
        </FullScreenWrapper>
    );
};

export default LeadChart;
